import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  ChargeInformationDto,
  ChargeInformationsService,
  ChargeType,
  CreateChargeInformationRequest,
  LoadLevel,
  ObservationTimeSeriesPeriod,
  UpdateChargeInformationCommand,
  VatClass
} from '@app/api/dmc';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { FormApiService } from '@app/core/services/form-api.service';
import { TranslatePipe } from "@ngx-translate/core";
import { InputGroupComponent } from "@shared/components/input/input-group/input-group.component";
import { InputDirective } from "@shared/components/input/input/input.directive";
import { InputDateDirective } from '@shared/components/input/date/input-date.directive';
import { LabelDirective } from "@shared/components/input/label/label.directive";
import { InputNumberDirective } from '@shared/components/input/number/input-number.directive';
import { SelectDirective } from "@shared/components/input/select/select.directive";
import { KeyValue } from '@shared/interfaces/key-value/key-value';
import { enumToKeyValue } from '@app/core/utils/enum-to-key-value';
import { isoToRealDate } from '@app/core/utils/iso-to-real-date';
import { dateGreaterThanEqualValidator } from '@shared/validators/date-greater-than-equal.validator';
import { HasUnsavedChanges } from '@app/core/guards/unsaved-changes.guard';

type ChargeInformationForm = {
  chargeType: FormControl<ChargeType | null>;
  chargeTypeId: FormControl<string | null>;
  description: FormControl<string | null>;
  longDescription: FormControl<string | null>;
  validFrom: FormControl<string | null>;
  observationPeriod: FormControl<ObservationTimeSeriesPeriod | null>;
  singleChargeSetupPrice: FormControl<number | null>;
  loadLevelLow: FormControl<number | null>;
  loadLevelHigh: FormControl<number | null>;
  loadLevelPeak: FormControl<number | null>;
  vatClass: FormControl<VatClass | null>;
  transparentInvoicing: FormControl<boolean>;
  ownerEnergyParty: FormControl<string | null>;
};

@Component({
  selector: 'app-chargeinformation-open-form',
  imports: [
    ReactiveFormsModule,
    InputDirective,
    InputDateDirective,
    InputGroupComponent,
    InputNumberDirective,
    LabelDirective,
    SelectDirective,
    TranslatePipe
  ],
  templateUrl: './chargeinformation-open-form.component.html',
  styleUrl: './chargeinformation-open-form.component.css'
})
export class ChargeinformationOpenFormComponent implements OnInit, OnDestroy, HasUnsavedChanges {
  public hasUnsavedChanges(): boolean {
    return this.form.dirty;
  }
  public currentChargeInformationDto?: ChargeInformationDto;
  public readonly firstValidDate = new Date(new Date().getFullYear(), new Date().getMonth() + 2, 1);
  public readonly firstValidDateString =
    `${this.firstValidDate.getFullYear()}-${String(this.firstValidDate.getMonth() + 1).padStart(2, '0')}-${String(this.firstValidDate.getDate()).padStart(2, '0')}`;

  private readonly subscriptions: Subscription = new Subscription();
  public form = new FormGroup<ChargeInformationForm>({
    chargeType: new FormControl<ChargeType | null>(null, Validators.required),
    chargeTypeId: new FormControl<string | null>(null, Validators.required),
    description: new FormControl<string | null>(null, Validators.required),
    longDescription: new FormControl<string | null>(null, Validators.required),
    validFrom: new FormControl<string | null>(null, [Validators.required, dateGreaterThanEqualValidator(this.firstValidDate)]),
    observationPeriod: new FormControl<ObservationTimeSeriesPeriod | null>(null),
    singleChargeSetupPrice: new FormControl<number | null>(null),
    loadLevelLow: new FormControl<number | null>(null),
    loadLevelHigh: new FormControl<number | null>(null),
    loadLevelPeak: new FormControl<number | null>(null),
    vatClass: new FormControl<VatClass | null>(null, Validators.required),
    transparentInvoicing: new FormControl(false, { nonNullable: true }),
    ownerEnergyParty: new FormControl<string | null>(null)
  });

  public id?: string;

  public chargeTypeOptions: KeyValue[] = enumToKeyValue(ChargeType, { omitKeys: ['NotSpecified'], prefixValue: 'chargeInformation.chargeTypes.' });
  public observationPeriodOptions: KeyValue[] = enumToKeyValue(ObservationTimeSeriesPeriod, { includeKeys: ['Day', 'Hour'], prefixValue: 'chargeInformation.observationPeriods.' });
  public vatClassOptions: KeyValue[] = enumToKeyValue(VatClass, { omitKeys: ['NotSpecified'], prefixValue: 'chargeInformation.vatClassOptions.' });
  constructor(
    private readonly formApiService: FormApiService,
    private readonly chargeInformationsService: ChargeInformationsService,
  ) {
    this.subscriptions.add(this.form.controls.chargeType.valueChanges.subscribe(this.handleChargeTypeChanged));
    this.subscriptions.add(this.form.controls.observationPeriod.valueChanges.subscribe(this.handleObservationPeriodChanged));
  }

  public ngOnInit(): void {
    if (this.currentChargeInformationDto) {
      this.initializeEditForm();
      this.updateForwardBillingState(this.form.controls.chargeType.value);
      return;
    }

    this.updateObservationPeriodState(this.form.controls.chargeType.value);
    this.updateForwardBillingState(this.form.controls.chargeType.value);
    this.updateCreatePriceValidators();
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public create(): void {
    if (this.form.valid) {
      const request = this.createRequest();
      if (!request) {
        this.form.markAllAsTouched();
        return;
      }

      this.formApiService.submit(
        this.form,
        () => this.chargeInformationsService.chargeInformationsPost(request),
        {
          successMessage: 'chargeInformation.chargeInformationCreated',
          closeModal: true,
        }
      );
    } else {
      this.form.markAllAsTouched();
    }
  }

  public saveChanges(): void {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }

    const command = this.createUpdateCommand();
    if (!command) {
      this.form.markAllAsTouched();
      return;
    }

    this.formApiService.submit(
      this.form,
      () => this.chargeInformationsService.chargeInformationsIdPut(this.currentChargeInformationDto!.id!, command),
      {
        successMessage: 'chargeInformation.chargeInformationUpdated',
        closeModal: true,
      }
    );
  }

  public get isCreateMode(): boolean {
    return !this.currentChargeInformationDto;
  }

  public get showObservationPeriodSelector(): boolean {
    return this.form.controls.chargeType.value === ChargeType.Tariff;
  }

  public get showCreateDetails(): boolean {
    if (!this.isCreateMode) {
      return true;
    }

    const chargeType = this.form.controls.chargeType.value;
    if (!chargeType) {
      return false;
    }

    return chargeType !== ChargeType.Tariff || this.form.controls.observationPeriod.value !== null;
  }

  public get isHourlyTariffSelection(): boolean {
    return this.form.controls.chargeType.value === ChargeType.Tariff &&
      this.form.controls.observationPeriod.value === ObservationTimeSeriesPeriod.Hour;
  }

  public get showCreateSinglePrice(): boolean {
    return this.isCreateMode && this.showCreateDetails && !this.isHourlyTariffSelection;
  }

  public get showCreateDifferentiatedPrices(): boolean {
    return this.isCreateMode && this.showCreateDetails && this.isHourlyTariffSelection;
  }

  public get showTransparentInvoicing(): boolean {
    return this.form.controls.chargeType.value !== ChargeType.Fee;
  }

  public toTranslationKey(prefix: string, value: string | null | undefined): string {
    return value ? `${prefix}${value.toLowerCase()}` : '';
  }

  private initializeEditForm(): void {
    this.form.patchValue(this.currentChargeInformationDto!);

    if (this.currentChargeInformationDto?.validFrom) {
      const isoDate = isoToRealDate(this.currentChargeInformationDto.validFrom);
      this.form.controls.validFrom.setValue(isoDate);
    }
  }

  private createRequest(): CreateChargeInformationRequest | null {
    const formData = this.form.getRawValue();
    const observationPeriod = this.resolveObservationPeriod();

    if (!formData.chargeType || !formData.chargeTypeId || !observationPeriod || !formData.validFrom || !formData.vatClass) {
      return null;
    }

    return {
      chargeInformation: {
        chargeTypeId: formData.chargeTypeId,
        chargeType: formData.chargeType,
        observationPeriod,
        description: formData.description ?? null,
        longDescription: formData.longDescription ?? null,
        vatClass: formData.vatClass,
        transparentInvoicing: formData.transparentInvoicing ?? false,
        taxIndicator: false,
        validFrom: formData.validFrom,
        validTo: null,
      },
      chargeSetup: {
        validFrom: formData.validFrom,
        loadLevelPrices: this.createChargeSetupPrices(),
      }
    };
  }

  private createChargeSetupPrices(): Array<{ price: number; loadLevel: LoadLevel }> {
    if (this.isHourlyTariffSelection) {
      return [
        { price: this.form.controls.loadLevelLow.value ?? 0, loadLevel: LoadLevel.Low },
        { price: this.form.controls.loadLevelHigh.value ?? 0, loadLevel: LoadLevel.High },
        { price: this.form.controls.loadLevelPeak.value ?? 0, loadLevel: LoadLevel.Peak }
      ];
    }

    return [{
      price: this.form.controls.singleChargeSetupPrice.value ?? 0,
      loadLevel: LoadLevel.None
    }];
  }

  private resolveObservationPeriod(): ObservationTimeSeriesPeriod | null {
    const chargeType = this.form.controls.chargeType.value;

    if (chargeType === ChargeType.Fee || chargeType === ChargeType.Subscription) {
      return ObservationTimeSeriesPeriod.Month;
    }

    return this.form.controls.observationPeriod.value;
  }

  private readonly handleChargeTypeChanged = (value: ChargeType | null) => {
    this.updateForwardBillingState(value);

    if (!this.isCreateMode) {
      return;
    }

    this.updateObservationPeriodState(value);
    this.updateCreatePriceValidators();
  };

  private readonly handleObservationPeriodChanged = () => {
    if (!this.isCreateMode) {
      return;
    }

    this.updateCreatePriceValidators();
  };

  private updateObservationPeriodState(chargeType: ChargeType | null): void {
    const observationPeriodControl = this.form.controls.observationPeriod;

    if (chargeType === ChargeType.Tariff) {
      observationPeriodControl.setValidators([Validators.required]);
      observationPeriodControl.setValue(null, { emitEvent: false });
    } else if (chargeType === ChargeType.Fee || chargeType === ChargeType.Subscription) {
      observationPeriodControl.clearValidators();
      observationPeriodControl.setValue(ObservationTimeSeriesPeriod.Month, { emitEvent: false });
    } else {
      observationPeriodControl.clearValidators();
      observationPeriodControl.setValue(null, { emitEvent: false });
    }

    observationPeriodControl.updateValueAndValidity({ emitEvent: false });
  }

  private updateForwardBillingState(chargeType: ChargeType | null): void {
    const transparentInvoicingControl = this.form.controls.transparentInvoicing;

    if (chargeType === ChargeType.Fee) {
      transparentInvoicingControl.setValue(false, { emitEvent: false });
    }
  }

  private updateCreatePriceValidators(): void {
    this.setRequiredValidator(this.form.controls.singleChargeSetupPrice, this.showCreateSinglePrice);
    this.setRequiredValidator(this.form.controls.loadLevelLow, this.showCreateDifferentiatedPrices);
    this.setRequiredValidator(this.form.controls.loadLevelHigh, this.showCreateDifferentiatedPrices);
    this.setRequiredValidator(this.form.controls.loadLevelPeak, this.showCreateDifferentiatedPrices);
  }

  private createUpdateCommand(): UpdateChargeInformationCommand | null {
    const formData = this.form.getRawValue();

    if (!formData.chargeType || !formData.chargeTypeId || !formData.observationPeriod || !formData.validFrom || !formData.vatClass) {
      return null;
    }

    return {
      chargeTypeId: formData.chargeTypeId,
      chargeType: formData.chargeType,
      observationPeriod: formData.observationPeriod,
      description: formData.description ?? null,
      longDescription: formData.longDescription ?? null,
      vatClass: formData.vatClass,
      transparentInvoicing: formData.transparentInvoicing,
      ownerEnergyParty: formData.ownerEnergyParty ?? null,
      validFrom: formData.validFrom,
    };
  }

  private setRequiredValidator(control: FormControl<number | null>, required: boolean): void {
    if (required) {
      control.setValidators([Validators.required]);
    } else {
      control.clearValidators();
    }

    control.updateValueAndValidity({ emitEvent: false });
  }
}
